import { EventEmitter, Injectable, Output } from '@angular/core';
import { MyfileApiService } from '../myfile-api.service';
import { AddressModel } from '../../models/address.model';
import { Subscription } from 'rxjs';
import { GlobalComponent } from 'src/app/global-component';
import { ContactModel } from '../../models/contact/contact.model';
import { RequisitionViewDataModel } from '../../models/requisition/requisition-view-data.model';
import { RequisitionModel } from '../../models/requisition/requisition.model';
import { ClientModel } from '../../models/members/client/client.model';
import { InsuranceCompany } from '../../models/members/client/insurance-company.model';
import { ClientModalService } from './client-modal.service';
import { FiniteStateMachineModel } from '../../models/finiteStateMachines/finite-state-machine.model';
import { UserModel } from '../../models/members/user/user.model';
import { EntityTypeEnum } from '../../enums/entity-type-id-enum';
import { FsmApiService } from '../fsmApi.service';
import { ContactModalService } from './contact-modal.service';
import { FiniteStateMachineModalService } from './finite-state-machine-modal.service';
import { RequisitionModalService } from './requisiton-modal.service';
import { UserModalService } from './user-modal.service';

@Injectable({
  providedIn: 'root'
})
export class OpenModalService {

  constructor(
    protected fsmApiService: FsmApiService,
    private myFileApiService: MyfileApiService,
    private myContactModalService: ContactModalService,
    private myRequisitionModalService: RequisitionModalService,
    private myClientModalService: ClientModalService,
    private myFiniteStateMachineModalService: FiniteStateMachineModalService,
    private myUserModalService: UserModalService
  ) { }

  @Output() isLoadingVal = new EventEmitter<boolean>();
  @Output() serviceSortUpdated = new EventEmitter<boolean>();

  serviceSort(service: any, sortColumn: any, sortDirection: any) {
    if (!sortColumn) {
      sortColumn = undefined;
    }
    if (!sortDirection) {
      sortDirection = undefined;
    }
    service.sortColumnDirective = sortColumn;
    service.sortDirection = sortDirection;
    this.serviceSortUpdated.emit(true);
  }

  openContactModal(contact: ContactModel, service?: any, listSearchColumn?: any, listSearchDirection?: any, disableEditing?: boolean, entityType?: EntityTypeEnum) {
    this.myContactModalService.openModal(contact, undefined, disableEditing, entityType);
    var sub: Subscription = this.myContactModalService.modelChanged.subscribe((result: any) => {
      if (result && service != undefined) {
        this.serviceSort(service, listSearchColumn, listSearchDirection);
      }
      sub.unsubscribe();
    });
  }

  editContactModal(service: any, modelId: string, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }

    //this call here ensures that the contact base model from the list with limited information is converted into a ContactModel which has all the contact's information before it is passed ot the modal component. 
    var sub: Subscription = this.myFileApiService.getContact(modelId)
      .subscribe((contact: ContactModel) => {
        if (contact) {

          //used for legacy contacts that where created before, and might not have and address field initialized. 
          if (contact.address == null || contact.address == undefined) {
            contact.address = new AddressModel();
          }

          this.openContactModal(contact, service, listSearchColumn, listSearchDirection);
        }
        sub.unsubscribe();
      });
  }

  newContactModal(service: any, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    var contactModel = new ContactModel;
    this.openContactModal(contactModel, service, listSearchColumn, listSearchDirection);
  }

  openRequisitionModal(requisition: RequisitionViewDataModel, service?: any, listSearchColumn?: any, listSearchDirection?: any, activeTab?: number) {
    this.myRequisitionModalService.openModal(requisition, undefined, activeTab);
    var sub: Subscription = this.myRequisitionModalService.modelChanged.subscribe((result: any) => {
      if (result) {
        this.serviceSort(service, listSearchColumn, listSearchDirection);
      }
      sub.unsubscribe();

    });
  }

  editRequisitionModal(service: any, modelId: string, listSearchColumn?: any, listSearchDirection?: any, event?: any, activeTab?: number) {
    if (event) {
      event.stopPropagation();
    }
    var sub: Subscription = this.myFileApiService.getRequisition(modelId)
      .subscribe((requisition: RequisitionModel|undefined) => {
        if (requisition) {
          if (requisition.id) {
            var modal: RequisitionViewDataModel = new RequisitionViewDataModel;
            modal.requisition = requisition;
            this.openRequisitionModal(modal, service, listSearchColumn, listSearchDirection, activeTab);
          }
        }
        this.isLoadingVal.emit(false);
        sub.unsubscribe();
      });
  }

  openClientModal(client: ClientModel, service?: any, listSearchColumn?: any, listSearchDirection?: any, activeTab?: number, disableEditing?: boolean, entityType?: EntityTypeEnum) {
    this.myClientModalService.openModal(client, undefined, activeTab, disableEditing, entityType);
    var sub: Subscription = this.myClientModalService.modelChanged.subscribe((result: any) => {
      if (result && service != undefined) {
        this.serviceSort(service, listSearchColumn, listSearchDirection);
      }
      sub.unsubscribe();
    });
  }

  editClientModal(service: any, modelId: string, listSearchColumn?: any, listSearchDirection?: any, event?: any, activeTab?: number) {
    if (event) {
      event.stopPropagation();
    }
    var sub: Subscription = this.myFileApiService.getClient(modelId)
      .subscribe((client: ClientModel | undefined) => {
        if (client) {
          if (client.address == null || client.address ==undefined) {
            client.address = new AddressModel();  //used for legacy clients. 
          }
          if (client.opposingCounsel.address == null || client.opposingCounsel.address == undefined) {
            client.opposingCounsel.address = new AddressModel();
          }
          if (client.insuranceCompany == null || client.insuranceCompany == undefined) {
            client.insuranceCompany = new InsuranceCompany();
          }

          //used to transform the date if the modal has one to the DD/MM/YYYY format
          if (client.dateOfBirth != null) {
            client.dateOfBirth = GlobalComponent.dateTransform(client.dateOfBirth, false);
          }
          if (client.dateOfLoss != undefined) {
            client.dateOfLoss = GlobalComponent.dateTransform(client.dateOfLoss, false);
          }

          this.openClientModal(client, service, listSearchColumn, listSearchDirection, activeTab);
        }
        sub.unsubscribe();
      });
  }

  newClientModal(service: any, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    let clientModel = new ClientModel;
    this.openClientModal(clientModel, service, listSearchColumn, listSearchDirection);
  }

  openFiniteStateMachineModal(machine: FiniteStateMachineModel, service?: any, listSearchColumn?: any, listSearchDirection?: any,) {
    this.myFiniteStateMachineModalService.openModal(machine);
    var sub: Subscription = this.myFiniteStateMachineModalService.modelChanged.subscribe((result: any) => {
      if (result) {
        this.serviceSort(service, listSearchColumn, listSearchDirection);
      }
      sub.unsubscribe();
    });
  }

  editFiniteStateMachineModal(service: any, modelId: string, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }

    this.fsmApiService.getFiniteStateMachines(modelId)
      .subscribe((machine: FiniteStateMachineModel | undefined) => {
        if (machine) {
          this.openFiniteStateMachineModal(machine, service, listSearchColumn, listSearchDirection);
        }
      });
  }

  newFiniteStateMachineModal(service: any, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    let machine = new FiniteStateMachineModel;
    this.openFiniteStateMachineModal(machine, service, listSearchColumn, listSearchDirection);
  }


  openUserModal(user: UserModel, tenantId: string, service?: any, listSearchColumn?: any, listSearchDirection?: any,) {
    this.myUserModalService.openModal(user, tenantId);
    var sub: Subscription = this.myUserModalService.modelChanged.subscribe((result: any) => {
      if (result) {
        this.serviceSort(service, listSearchColumn, listSearchDirection);
      }
      sub.unsubscribe();
    });
  }

  editUserModal(modelId: string, tenantId: string | undefined, service: any, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    //put here so you can pass no tenantID, so this call does not have to be repeated each time in parent components. 
    if (tenantId === undefined) {
      var sub: Subscription = this.myFileApiService.getBusinessUnitForCurrentUser().subscribe(result => {
        if (result) {
          result.tenantId;
          this.editUserModal(modelId, result.tenantId, service, listSearchColumn, listSearchDirection, event);
          sub.unsubscribe();
        }
      })
    } else {
      //this call here ensures that the contact base model from the list with limited information is converted into a ContactModel which has all the contact's information before it is passed ot the modal component. 
      var sub: Subscription = this.myFileApiService.getUser(modelId, tenantId).subscribe((userModel: UserModel | undefined) => {
        if (userModel) {
          //used for legacy contacts that where created before, and might not have and address field initialized. 
          if (userModel.address == null || userModel.address == undefined) {
            userModel.address = new AddressModel();
          }
          this.openUserModal(userModel, tenantId, service, listSearchColumn, listSearchDirection);
          sub.unsubscribe();
        }
      });
    }
  }

  newUserModal(currentTenantId: string, service: any, listSearchColumn?: any, listSearchDirection?: any, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    var userModel = new UserModel;
    this.openUserModal(userModel, currentTenantId, service, listSearchColumn, listSearchDirection);
  }

}
